import { getProjectsStats, getProjectStats } from '@lib/api_ts/statsApi';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import HalfDonutCard from './HalfDonutCard';
import HistogramCard from './HistogramCard';
import { useParams } from 'react-router-dom';
import { Loading } from '@components/atomic/atoms';

export type ChartElementType = {
	key: string;
	label: string;
	value: number;
	percentage?: number;
	color: string;
	tooltipExtraData?: {
		key: string;
		label: string;
		value: number;
	}[];
};

export type LandingChartType = {
	key: string;
	title: string;
	type: 'half-donut' | 'histogram';
	elements: ChartElementType[];
	extraData?: {
		[key: string]: number | string | null;
	}[];
};

interface LandingChartsProps {}

const LandingCharts: React.FC<LandingChartsProps> = () => {
	const { projectId } = useParams<{ projectId?: string }>();

	const { data: projectStats, isPending: isPendingProjectStats } = useQuery({
		queryKey: ['projects-stats', projectId],
		queryFn: () => (!projectId ? getProjectsStats() : getProjectStats(projectId)),
	});

	// console.log('LandingCharts render', projectStats);

	if (isPendingProjectStats) {
		return (
			<div
				style={{
					display: 'grid',
					gridTemplateColumns: '1fr 1fr',
					gap: '3rem',
				}}
			>
				{Array.from({ length: 3 }).map((_, index) => {
					const isLast = index === 2;
					return (
						<div
							key={index}
							style={{
								borderRadius: '12px',
								// minHeight: !isLast ? '330px' : '108px',
								minHeight: '330px',
								backgroundColor: '#fff',
								...(!isLast ? {} : { gridColumn: 'span 2' }),
							}}
						>
							<Loading />
						</div>
					);
				})}

				{/* <div style={{ borderRadius: '12px', gridColumn: 'span 2' }}>
					<Loading />
				</div> */}
			</div>
		);
	}

	const landingCharts: LandingChartType[] = [
		{
			key: 'execution',
			title: 'Avanzamento',
			type: 'half-donut' as const,
			elements: [
				{
					key: 'finalAmount',
					label: 'Spesa effettuata',
					value: projectStats.finalAmount,
					percentage: (projectStats.finalAmount / projectStats.totalAmount) * 100,
					color: 'var(--np-chart-color--dark-blue)',
				},
				{
					key: 'amountToBeSpent',
					label: 'Spesa da effettuare',
					value: projectStats.totalAmount - projectStats.finalAmount,
					percentage: Number.isFinite(((projectStats.totalAmount - projectStats.finalAmount) / projectStats.totalAmount) * 100) ? ((projectStats.totalAmount - projectStats.finalAmount) / projectStats.totalAmount) * 100 : 0,
					color: 'var(--np-chart-color--blue)',
				},
			],
			extraData: [
				{
					key: 'total',
					value: projectStats.totalAmount,
				},
				{
					key: 'lastDateFtImport',
					value: projectStats.lastDateFtImport,
				},
				{
					key: 'lastDateTsImport',
					value: projectStats.lastDateTsImport,
				},
				{
					key: 'lastSalFt',
					value: projectStats.lastSalFt,
				},
				{
					key: 'lastSalTs',
					value: projectStats.lastSalTs,
				},
			],
		},
		{
			key: 'summary',
			title: 'Controllo',
			type: 'half-donut' as const,
			elements: [
				{
					key: 'inProgressAmount',
					label: 'In lavorazione',
					value: projectStats.inProgressAmount,
					color: 'var(--np-chart-color--dark-blue)',
					percentage: (projectStats.inProgressAmount / projectStats.finalAmount) * 100,
				},
				{
					key: 'compliantAmount',
					label: 'Eleggibile',
					value: projectStats.compliantAmount,
					percentage: (projectStats.compliantAmount / projectStats.finalAmount) * 100,
					color: 'var(--np-chart-color--blue)',
				},
				{
					key: 'notCompliantAmount',
					label: 'Non eleggibile',
					value: projectStats.notCompliantAmount,
					percentage: (projectStats.notCompliantAmount / projectStats.finalAmount) * 100,
					color: 'var(--np-chart-color--light-blue)',
				},
			],
			extraData: [
				{
					key: 'total',
					value: projectStats.finalAmount,
				},
			],
		},
		{
			key: 'reporting',
			title: 'Rendicontazione',
			type: 'histogram' as const,
			elements: [
				{
					key: 'advanceCashedIn',
					label: 'Anticipo su contributo',
					value: projectStats.advanceCashedIn,
					color: 'var(--np-chart-color--green)',
				},
				{
					key: 'reportedAmount',
					label: 'Spese rendicontate',
					value: projectStats.reportedAmount,
					color: 'var(--np-chart-color--purple)',
					tooltipExtraData: [
						{
							key: 'reportedRealCost',
							label: 'Costi reali rendicontati',
							value: projectStats.reportedAmount - projectStats.reportedIndirectCost,
						},
						{
							key: 'reportedIndirectCost',
							label: 'Costi indiretti rendicontati',
							value: projectStats.reportedIndirectCost,
						},
					],
				},
				{
					key: 'empty_0',
					label: '',
					value: null,
					color: 'transparent',
				},
				{
					key: 'requestedAmount',
					label: 'Contributo richiesto',
					value: projectStats.requestedAmount,
					color: 'var(--np-chart-color--dark-blue)',
				},
				{
					key: 'totalApprovedAmount',
					label: 'Richiesto approvato',
					value: projectStats.totalApprovedAmount,
					color: 'var(--np-chart-color--blue)',
				},
				{
					key: 'cashedIn',
					label: 'Richiesto incassato',
					value: projectStats.cashedIn,
					color: 'var(--np-chart-color--medium-blue)',
				},
				{
					key: 'notApprovedAmount',
					label: 'Richiesto non approvato',
					value: projectStats.requestedAmount - projectStats.totalApprovedAmount,
					color: 'var(--np-chart-color--light-blue)',
				},
			],
			extraData: [
				{
					key: 'lastSalStatement',
					value: projectStats.lastSalStatement,
				},
				{
					key: 'lastDateStatement',
					value: projectStats.lastDateStatement,
				},
			],
		},
	];

	return (
		<div
			style={{
				display: 'grid',
				gridTemplateColumns: '1fr 1fr',
				gap: '3rem',
			}}
		>
			{landingCharts.map((chart) => {
				switch (chart.type) {
					case 'half-donut':
						return <HalfDonutCard key={chart.key} chart={chart} />;
					case 'histogram':
						return <HistogramCard key={chart.key} chart={chart} />;
					default:
						return null;
				}
			})}
		</div>
	);
};

export default LandingCharts;
